// ===========================================
// Bilans & États — Page rapports (refonte UX cohérente)
// 6 tabs : Bordereau courtier / Bordereau assureurs / Clients actifs / Point journalier (date saisie) / Production du jour (date de production) / Paiements période
// ===========================================
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Tabs, Table, Select, Button, DatePicker, Grid, Tag, Dropdown, Empty,
    Modal, Tooltip, Popconfirm, Input, Row, Col,
} from 'antd';
import {
    BarChartOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    FileTextOutlined, BankOutlined, TeamOutlined, CalendarOutlined, WalletOutlined,
    SendOutlined, CheckCircleOutlined, ExclamationCircleOutlined, RollbackOutlined,
    DollarOutlined, ClearOutlined, QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { bilansService, agencesService, compagniesService, typesAssuranceService } from '../../common/services';
import { useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES, PDF_FOOT_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatPhone, formatMontant } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './bilans.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const getClientName = (record) => {
    if (!record) return '—';
    if (record.type === 'entreprise') return record.raisonSociale || record.nom || '—';
    return `${record.nom || ''} ${record.prenom || ''}`.trim() || '—';
};

// antd v5 = dayjs : on convertit aux frontières des DatePicker (l'état reste en moment).
// Conversion par timestamp -> robuste quelle que soit la librairie de l'objet source.
const toDayjs = (m) => (m && m.valueOf ? dayjs(m.valueOf()) : null);
const toMoment = (d) => (d && d.valueOf ? moment(d.valueOf()) : null);

const Bilans = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'bordereau-courtier';
    const [activeTab, setActiveTabState] = useState(tabFromUrl);

    useEffect(() => {
        setActiveTabState(tabFromUrl);
    }, [tabFromUrl]);

    const setActiveTab = (tab) => {
        setActiveTabState(tab);
        setSearchParams({ tab });
    };

    const [data, setData] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agences, setAgences] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [typesAssurance, setTypesAssurance] = useState([]);
    const [selectedAgence, setSelectedAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [selectedCompagnie, setSelectedCompagnie] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [dateRange, setDateRange] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [versementModalOpen, setVersementModalOpen] = useState(false);
    const [versementDate, setVersementDate] = useState(moment());
    const [versementSubmitting, setVersementSubmitting] = useState(false);
    const [pjMeta, setPjMeta] = useState({ totaux: null, agentNom: '', dateProduction: null });
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    useEffect(() => {
        Promise.all([
            agencesService.getAll().catch(() => []),
            compagniesService.getAll().catch(() => []),
            typesAssuranceService.getAll().catch(() => []),
        ]).then(([agRes, compRes, typeRes]) => {
            setAgences(Array.isArray(agRes) ? agRes : agRes?.data || []);
            setCompagnies(Array.isArray(compRes) ? compRes : compRes?.data || []);
            setTypesAssurance(Array.isArray(typeRes) ? typeRes : typeRes?.data || []);
        });
    }, []);

    const fetchData = async () => {
        const params = {};
        if (selectedAgence !== 'all') params.agence = selectedAgence;
        if (selectedCompagnie !== 'all') params.compagnie = selectedCompagnie;
        if (selectedType !== 'all') params.typeAssurance = selectedType;
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.start = dateRange[0].format('YYYY-MM-DD');
            params.end = dateRange[1].format('YYYY-MM-DD');
        }


        setLoading(true);
        try {
            let res;
            switch (activeTab) {
                case 'bordereau-courtier': {
                    res = await bilansService.bordereauCourtier(params);
                    const courtierRaw = res?.data?.data || res?.data || [];
                    const courtierFlat = [];
                    (Array.isArray(courtierRaw) ? courtierRaw : []).forEach((agence) => {
                        (agence.contrats || []).forEach((c) => {
                            courtierFlat.push({
                                _id: c._id,
                                numeroPolice: c.numeroPolice,
                                clientNom: c.client ? `${c.client.nom || ''} ${c.client.prenom || ''}`.trim() : '—',
                                compagnie: c.compagnie || '—',
                                compagnieId: c.compagnieId,
                                typeAssurance: c.type || '—',
                                primeNette: c.primeNette || 0,
                                primeTTC: c.primeTTC || 0,
                                accessoiresCourtier: c.accessoiresCourtier || 0,
                                commission: c.commissionCourtier || 0,
                                montantReverseAssureur: c.montantReverseAssureur || 0,
                                montantPaye: c.montantPaye || 0,
                                estPaye: !!c.estPaye,
                                estVerse: !!c.estVerse,
                                versement: c.versement || null,
                                dateEffet: c.dateEffet,
                            });
                        });
                    });
                    courtierFlat.sort((a, b) => new Date(b.dateEffet || 0) - new Date(a.dateEffet || 0));
                    setData(courtierFlat);
                    break;
                }
                case 'bordereau-assureurs': {
                    res = await bilansService.bordereauAssureur(params);
                    const assureurRaw = res?.data?.data || res?.data || [];
                    const assureurMapped = Array.isArray(assureurRaw) ? assureurRaw.map((a) => ({
                        _id: a._id,
                        compagnie: a.compagnieNom || '—',
                        nbContrats: a.nombreContrats || 0,
                        totalPrimes: a.totalPrimeNette || 0,
                        totalCommissions: a.totalCommission || 0,
                        totalAccessoiresAssureur: a.totalAccessoiresAssureur || 0,
                        netAReverser: a.totalReverseAssureur || 0,
                        versements: Array.isArray(a.versements) ? a.versements : [],
                    })) : [];
                    assureurMapped.sort((a, b) => (b.totalPrimes || 0) - (a.totalPrimes || 0));
                    setData(assureurMapped);
                    break;
                }
                case 'clients-actifs': {
                    res = await bilansService.clientsActifs(params);
                    const clientsRaw = res?.data?.clients || res?.data || res || [];
                    const clientsSorted = Array.isArray(clientsRaw)
                        ? [...clientsRaw].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                        : [];
                    setData(clientsSorted);
                    break;
                }
                case 'point-journalier': {
                    const pjParams = { ...params };
                    if (pjParams.start) { pjParams.date = pjParams.start; delete pjParams.start; delete pjParams.end; }
                    res = await bilansService.pointJournalier(pjParams);
                    const pjRaw = res?.data || res || {};
                    const pjList = pjRaw.nouveauxContrats?.contrats || pjRaw.contrats || pjRaw.data || (Array.isArray(pjRaw) ? pjRaw : []);
                    const pjSorted = Array.isArray(pjList) ? [...pjList].sort((a, b) => new Date(b.dateEffet || 0) - new Date(a.dateEffet || 0)) : [];
                    setPjMeta({
                        totaux: pjRaw.totaux || null,
                        agentNom: pjRaw.agentNom || '',
                        dateProduction: pjRaw.date || pjParams.date || new Date().toISOString(),
                    });
                    setData(pjSorted);
                    break;
                }
                case 'production-jour': {
                    const prParams = { ...params };
                    if (prParams.start) { prParams.date = prParams.start; delete prParams.start; delete prParams.end; }
                    res = await bilansService.productionJour(prParams);
                    const prRaw = res?.data || res || {};
                    const prList = prRaw.nouveauxContrats?.contrats || prRaw.contrats || prRaw.data || (Array.isArray(prRaw) ? prRaw : []);
                    const prSorted = Array.isArray(prList) ? [...prList].sort((a, b) => new Date(b.dateEffet || 0) - new Date(a.dateEffet || 0)) : [];
                    setPjMeta({
                        totaux: prRaw.totaux || null,
                        agentNom: prRaw.agentNom || '',
                        dateProduction: prRaw.date || prParams.date || new Date().toISOString(),
                    });
                    setData(prSorted);
                    break;
                }
                case 'paiements-periode': {
                    res = await bilansService.paiementsPeriode(params);
                    const ppRaw = res?.data || res || {};
                    const ppList = ppRaw.reglements || ppRaw.data || (Array.isArray(ppRaw) ? ppRaw : []);
                    const ppSorted = Array.isArray(ppList) ? [...ppList].sort((a, b) => new Date(b.dateReglement || 0) - new Date(a.dateReglement || 0)) : [];
                    setData(ppSorted);
                    break;
                }
                default:
                    setData([]);
            }
        } catch (error) {
            console.error('Erreur chargement bilans:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, selectedAgence, selectedCompagnie, selectedType, dateRange]);

    useEffect(() => {
        setSelectedRowKeys([]);
    }, [activeTab, selectedAgence, selectedCompagnie, selectedType, dateRange]);

    // ─── Filtres ──────────────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (selectedAgence !== 'all') n++;
        if (selectedCompagnie !== 'all') n++;
        if (selectedType !== 'all') n++;
        if (dateRange) n++;
        return n;
    }, [selectedAgence, selectedCompagnie, selectedType, dateRange]);

    const clearAllFilters = () => {
        setSelectedAgence('all');
        setSelectedCompagnie('all');
        setSelectedType('all');
        setDateRange(null);
    };

    // Titre d'export enrichi de la compagnie et du type d'assurance sélectionnés
    const buildTitreExport = (title) => {
        const compagnieNom = selectedCompagnie !== 'all'
            ? (compagnies.find((c) => c._id === selectedCompagnie)?.nom || '')
            : '';
        const typeNom = selectedType !== 'all'
            ? (typesAssurance.find((t) => t._id === selectedType)?.nom || '')
            : '';
        return [title, compagnieNom, typeNom.toUpperCase()].filter(Boolean).join(' - ');
    };

    // ─── Statut courtier (badge) ──────────────────────────────────────
    const renderStatutCourtier = (_, r) => {
        if (r.estVerse) {
            return (
                <Tooltip title={r.versement?.numeroBordereau ? `Bordereau ${r.versement.numeroBordereau}` : 'Versé au bordereau assureur'}>
                    <Tag icon={<CheckCircleOutlined />} color="green">Versé</Tag>
                </Tooltip>
            );
        }
        if (!r.estPaye) {
            const reste = (r.primeTTC || 0) - (r.montantPaye || 0);
            return (
                <Tooltip title={`Reste à payer : ${formatMontant(reste)}`}>
                    <Tag icon={<ExclamationCircleOutlined />} color="red">Impayé</Tag>
                </Tooltip>
            );
        }
        return <Tag color="blue">À verser</Tag>;
    };

    // ─── Colonnes par tab ─────────────────────────────────────────────
    const bordereauCourtierColumns = [
        { title: 'N° Police', dataIndex: 'numeroPolice', key: 'police', width: 130, render: (t) => <span className="bilan-mono">{t || '—'}</span> },
        { title: 'Client', dataIndex: 'clientNom', key: 'client', width: 200, ellipsis: true },
        { title: 'Compagnie', dataIndex: 'compagnie', key: 'compagnie', width: 160, ellipsis: true, responsive: ['md'] },
        { title: 'Type', dataIndex: 'typeAssurance', key: 'type', width: 150, render: (t) => <Tag color="blue">{t || '—'}</Tag>, responsive: ['md'] },
        { title: 'Prime nette', dataIndex: 'primeNette', key: 'primeNette', width: 130, align: 'right', sorter: (a, b) => (a.primeNette || 0) - (b.primeNette || 0), render: (v) => <span className="bilan-amount">{formatMontant(v || 0)}</span> },
        { title: 'Access. courtier', dataIndex: 'accessoiresCourtier', key: 'accCourtier', width: 140, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span>, responsive: ['lg'] },
        { title: 'Commission', dataIndex: 'commission', key: 'commission', width: 130, align: 'right', sorter: (a, b) => (a.commission || 0) - (b.commission || 0), render: (v) => <span className="bilan-amount is-positive">{formatMontant(v || 0)}</span> },
        { title: 'Statut', key: 'statut', width: 110, align: 'center', render: renderStatutCourtier },
        { title: 'Date', dataIndex: 'dateEffet', key: 'date', width: 110, render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—', responsive: ['lg'] },
    ];

    const bordereauAssureursColumns = [
        { title: 'Compagnie', dataIndex: 'compagnie', key: 'compagnie', render: (v) => <span className="bilan-cell-name">{v || '—'}</span>, ellipsis: true },
        { title: 'Nb contrats', dataIndex: 'nbContrats', key: 'nbContrats', width: 110, align: 'center', sorter: (a, b) => (a.nbContrats || 0) - (b.nbContrats || 0), render: (v) => <span className={`bilan-cell-nb ${v > 0 ? 'has-contrats' : ''}`}>{v || 0}</span> },
        { title: 'Total primes', dataIndex: 'totalPrimes', key: 'totalPrimes', width: 150, align: 'right', sorter: (a, b) => (a.totalPrimes || 0) - (b.totalPrimes || 0), render: (v) => <span className="bilan-amount">{formatMontant(v || 0)}</span> },
        { title: 'Commissions', dataIndex: 'totalCommissions', key: 'totalCommissions', width: 150, align: 'right', sorter: (a, b) => (a.totalCommissions || 0) - (b.totalCommissions || 0), render: (v) => <span className="bilan-amount is-positive">{formatMontant(v || 0)}</span> },
        { title: 'Access. assureur', dataIndex: 'totalAccessoiresAssureur', key: 'totalAccessoiresAssureur', width: 150, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span>, responsive: ['md'] },
        { title: 'Net à reverser', dataIndex: 'netAReverser', key: 'netAReverser', width: 160, align: 'right', sorter: (a, b) => (a.netAReverser || 0) - (b.netAReverser || 0), render: (v) => <span className="bilan-amount is-warning">{formatMontant(v || 0)}</span> },
    ];

    const clientsActifsColumns = [
        { title: 'Code', dataIndex: 'code', key: 'code', width: 110, render: (t) => <span className="bilan-mono">{t || '—'}</span> },
        { title: 'Nom / Raison sociale', key: 'nom', render: (_, r) => <span className="bilan-cell-name">{getClientName(r)}</span>, ellipsis: true },
        { title: 'Téléphone', dataIndex: 'telephone', key: 'telephone', width: 140, render: (tel) => formatPhone(tel) || '—' },
        { title: 'Compagnie', dataIndex: 'compagniesLabel', key: 'compagnies', width: 200, render: (v) => v || '—', ellipsis: true, responsive: ['md'] },
        { title: 'Contrats actifs', dataIndex: 'nbContratsActifs', key: 'nb', width: 130, align: 'center', sorter: (a, b) => (a.nbContratsActifs || 0) - (b.nbContratsActifs || 0), render: (v) => <span className={`bilan-cell-nb ${v > 0 ? 'has-contrats' : ''}`}>{v || 0}</span> },
        { title: 'Total primes', dataIndex: 'totalPrimes', key: 'primes', width: 150, align: 'right', sorter: (a, b) => (a.totalPrimes || 0) - (b.totalPrimes || 0), render: (v) => <span className="bilan-amount">{formatMontant(v || 0)}</span> },
        { title: 'Inscrit', dataIndex: 'createdAt', key: 'createdAt', width: 110, render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—', responsive: ['lg'] },
    ];

    const pointJournalierColumns = [
        { title: 'N° Police', dataIndex: 'numeroPolice', key: 'police', width: 130, render: (t) => <span className="bilan-mono">{t || '—'}</span> },
        { title: 'Client', key: 'client', render: (_, r) => <span className="bilan-cell-name">{r.client ? `${r.client.nom || ''} ${r.client.prenom || ''}`.trim() || '—' : '—'}</span>, ellipsis: true },
        { title: 'Type', dataIndex: ['typeAssurance', 'nom'], key: 'type', width: 160, render: (t) => <Tag color="blue">{t || '—'}</Tag>, responsive: ['md'] },
        { title: 'Prime TTC', dataIndex: 'primeTTC', key: 'prime', width: 140, align: 'right', sorter: (a, b) => (a.primeTTC || 0) - (b.primeTTC || 0), render: (v) => <span className="bilan-amount">{formatMontant(v || 0)}</span> },
        { title: 'Access. courtier', dataIndex: 'accessoiresCourtier', key: 'accCourtier', width: 140, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span>, responsive: ['lg'] },
        { title: 'Espèces', dataIndex: 'especes', key: 'especes', width: 110, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span> },
        { title: 'Chèque', dataIndex: 'cheque', key: 'cheque', width: 110, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span> },
        { title: 'Virement', dataIndex: 'virement', key: 'virement', width: 110, align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span> },
        { title: 'Date effet', dataIndex: 'dateEffet', key: 'dateEffet', width: 110, render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—' },
    ];

    const paiementsPeriodeColumns = [
        { title: 'Date', dataIndex: 'dateReglement', key: 'date', width: 110, render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—' },
        { title: 'Client', key: 'client', render: (_, r) => <span className="bilan-cell-name">{r.client ? `${r.client.nom || ''} ${r.client.prenom || ''}`.trim() || '—' : '—'}</span>, ellipsis: true },
        { title: 'N° Police', dataIndex: ['contrat', 'numeroPolice'], key: 'police', width: 130, render: (t) => <span className="bilan-mono">{t || '—'}</span> },
        { title: 'Compagnie', key: 'compagnie', render: (_, r) => r.contrat?.compagnie?.nom || '—', ellipsis: true, responsive: ['lg'] },
        { title: 'Agence', key: 'agence', render: (_, r) => r.agence?.nom || '—', ellipsis: true, responsive: ['lg'] },
        { title: 'Montant', dataIndex: 'montant', key: 'montant', width: 140, align: 'right', sorter: (a, b) => (a.montant || 0) - (b.montant || 0), render: (v) => <span className="bilan-amount is-positive">{formatMontant(v || 0)}</span> },
        { title: 'Mode', dataIndex: 'moyenPaiement', key: 'mode', width: 130, render: (m) => m ? <Tag>{m}</Tag> : '—', responsive: ['md'] },
    ];

    const getColumns = () => {
        switch (activeTab) {
            case 'bordereau-courtier':  return bordereauCourtierColumns;
            case 'bordereau-assureurs': return bordereauAssureursColumns;
            case 'clients-actifs':      return clientsActifsColumns;
            case 'point-journalier':    return pointJournalierColumns;
            case 'production-jour':     return pointJournalierColumns;
            case 'paiements-periode':   return paiementsPeriodeColumns;
            default: return bordereauCourtierColumns;
        }
    };

    // ─── Versement bordereau ──────────────────────────────────────────
    const handleVerser = async () => {
        if (!selectedRowKeys.length) {
            toast.warning('Aucun contrat sélectionné');
            return;
        }
        setVersementSubmitting(true);
        try {
            const dateStr = versementDate ? versementDate.format('YYYY-MM-DD') : null;
            const res = await bilansService.verserBordereauAssureur(selectedRowKeys, dateStr);
            const payload = res?.data || res || {};
            toast.success(`${payload.nombreVerses || 0} contrat(s) versé(s) — Bordereau ${payload.numeroBordereau || ''}`);
            if (payload.nombreRejetes > 0) {
                toast.warning(`${payload.nombreRejetes} contrat(s) rejeté(s) (impayés ou déjà versés)`);
            }
            setVersementModalOpen(false);
            setSelectedRowKeys([]);
            fetchData();
        } catch (e) {
            console.error('Erreur versement:', e);
            toast.error(e?.message || 'Échec du versement');
        } finally {
            setVersementSubmitting(false);
        }
    };

    const handleAnnulerVersement = async (id, raison) => {
        try {
            await bilansService.annulerVersement(id, raison || '');
            toast.success('Versement annulé');
            fetchData();
        } catch (e) {
            console.error('Erreur annulation:', e);
            toast.error(e?.message || "Échec de l'annulation");
        }
    };

    const rowSelection = activeTab === 'bordereau-courtier' ? {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        getCheckboxProps: (record) => ({
            disabled: !record.estPaye || record.estVerse,
            name: record.numeroPolice,
        }),
    } : undefined;

    const eligiblesCount = useMemo(() => {
        if (activeTab !== 'bordereau-courtier') return 0;
        return data.filter((d) => d.estPaye && !d.estVerse).length;
    }, [activeTab, data]);

    const renderVersementsDetails = (record) => {
        const versements = record.versements || [];
        const cols = [
            { title: 'N° Bordereau', dataIndex: 'numeroBordereau', key: 'num', render: (t) => <span className="bilan-mono">{t || '—'}</span> },
            { title: 'Date versement', dataIndex: 'dateVersement', key: 'dv', render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—' },
            { title: 'N° Police', dataIndex: 'numeroPolice', key: 'np', render: (t) => <span className="bilan-mono">{t || '—'}</span> },
            { title: 'Client', key: 'client', render: (_, r) => r.client ? `${r.client.nom || ''} ${r.client.prenom || ''}`.trim() : '—' },
            { title: 'Prime nette', dataIndex: 'primeNette', key: 'pn', align: 'right', render: (v) => <span className="bilan-amount">{formatMontant(v || 0)}</span> },
            { title: 'Access. assureur', dataIndex: 'accessoiresAssureur', key: 'aa', align: 'right', render: (v) => <span className="bilan-amount-soft">{formatMontant(v || 0)}</span> },
            { title: 'Net reversé', dataIndex: 'montantReverse', key: 'mr', align: 'right', render: (v) => <strong className="bilan-amount">{formatMontant(v || 0)}</strong> },
            {
                title: 'Action', key: 'action',
                render: (_, r) => (
                    <Popconfirm
                        title="Annuler ce versement ?"
                        description={
                            <Input.TextArea
                                placeholder="Raison de l'annulation (optionnel)"
                                rows={2}
                                onChange={(e) => { r._raison = e.target.value; }}
                            />
                        }
                        okText="Confirmer"
                        cancelText="Retour"
                        onConfirm={() => handleAnnulerVersement(r._id, r._raison)}
                    >
                        <Button danger size="small" icon={<RollbackOutlined />}>Annuler</Button>
                    </Popconfirm>
                ),
            },
        ];
        return (
            <Table
                size="small"
                dataSource={versements}
                columns={cols}
                rowKey="_id"
                pagination={false}
                className="bilan-versements-table"
            />
        );
    };

    // ─── Exports (logique préservée) ──────────────────────────────────
    const fmt = (v) => (v || 0).toLocaleString('fr-FR').replace(/\s/g, ' ');

    const tabTitles = {
        'bordereau-courtier': 'BORDEREAU COURTIER',
        'bordereau-assureurs': 'BORDEREAU ASSUREURS',
        'clients-actifs': 'CLIENTS ACTIFS',
        'point-journalier': 'POINT JOURNALIER',
        'production-jour': 'PRODUCTION DU JOUR',
        'paiements-periode': 'PAIEMENTS PAR PERIODE',
    };

    const sumOf = (arr, key) => arr.reduce((s, d) => s + (Number(d[key]) || 0), 0);
    const totalCellStyle = { halign: 'right', fontStyle: 'bold', fillColor: false, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.3 };

    const getExportConfig = () => {
        switch (activeTab) {
            case 'bordereau-courtier':
                return {
                    headers: ['N° Police', 'Client', 'Compagnie', 'Type', 'Prime nette', 'Access. courtier', 'Commission', 'Date'],
                    rows: data.map((d) => [d.numeroPolice, d.clientNom, d.compagnie, d.typeAssurance, fmt(d.primeNette), fmt(d.accessoiresCourtier), fmt(d.commission), d.dateEffet ? new Date(d.dateEffet).toLocaleDateString('fr-FR') : '']),
                    widths: [22, 24, 22, 22, 18, 18, 18, 14],
                    numericCols: [4, 5, 6],
                    foot: [
                        { content: 'TOTAL', colSpan: 4, styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'primeNette')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'accessoiresCourtier')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'commission')), styles: totalCellStyle },
                        { content: '', styles: totalCellStyle },
                    ],
                };
            case 'bordereau-assureurs':
                return {
                    headers: ['Compagnie', 'Nb contrats', 'Total primes', 'Total commissions', 'Access. assureur', 'Net a reverser'],
                    rows: data.map((d) => [d.compagnie, d.nbContrats, fmt(d.totalPrimes), fmt(d.totalCommissions), fmt(d.totalAccessoiresAssureur), fmt(d.netAReverser)]),
                    widths: [26, 14, 18, 18, 18, 20],
                    numericCols: [1, 2, 3, 4, 5],
                    foot: [
                        { content: 'TOTAL', styles: totalCellStyle },
                        { content: String(sumOf(data, 'nbContrats')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'totalPrimes')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'totalCommissions')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'totalAccessoiresAssureur')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'netAReverser')), styles: totalCellStyle },
                    ],
                };
            case 'clients-actifs':
                return {
                    headers: ['Code', 'Nom / Raison sociale', 'Téléphone', 'Compagnie', 'Nb contrats actifs', 'Total primes', 'Date'],
                    rows: data.map((d) => [d.code, getClientName(d), formatPhone(d.telephone), d.compagniesLabel || '-', d.nbContratsActifs, fmt(d.totalPrimes), d.createdAt ? new Date(d.createdAt).toLocaleDateString('fr-FR') : '']),
                    widths: [16, 24, 18, 22, 14, 18, 14],
                    numericCols: [4, 5],
                    foot: [
                        { content: 'TOTAL', colSpan: 4, styles: totalCellStyle },
                        { content: String(sumOf(data, 'nbContratsActifs')), styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'totalPrimes')), styles: totalCellStyle },
                        { content: '', styles: totalCellStyle },
                    ],
                };
            case 'point-journalier':
            case 'production-jour':
                return {
                    headers: ['N° Police', 'Client', 'Type', 'Prime TTC', 'Date effet'],
                    rows: data.map((d) => [d.numeroPolice, d.client ? `${d.client.nom || ''} ${d.client.prenom || ''}`.trim() : '', d.typeAssurance?.nom || '', fmt(d.primeTTC), d.dateEffet ? new Date(d.dateEffet).toLocaleDateString('fr-FR') : '']),
                    widths: [22, 28, 24, 18, 16],
                    numericCols: [3],
                    foot: null,
                };
            case 'paiements-periode':
                return {
                    headers: ['Date', 'Client', 'N° Police', 'Compagnie', 'Agence', 'Montant', 'Mode'],
                    rows: data.map((d) => [
                        d.dateReglement ? new Date(d.dateReglement).toLocaleDateString('fr-FR') : '',
                        d.client ? `${d.client.nom || ''} ${d.client.prenom || ''}`.trim() : '',
                        d.contrat?.numeroPolice || '',
                        d.contrat?.compagnie?.nom || '',
                        d.agence?.nom || '',
                        fmt(d.montant),
                        d.moyenPaiement || '',
                    ]),
                    widths: [14, 22, 18, 22, 20, 16, 12],
                    numericCols: [5],
                    foot: [
                        { content: 'TOTAL', colSpan: 5, styles: totalCellStyle },
                        { content: fmt(sumOf(data, 'montant')), styles: totalCellStyle },
                        { content: '', styles: totalCellStyle },
                    ],
                };
            default:
                return { headers: [], rows: [], widths: [], numericCols: [] };
        }
    };

    const handleExportPDFTableauJournalier = () => {
        const doc = new jsPDF({ orientation: 'portrait' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        // Espace insécable ( ) comme séparateur de milliers : empêche les montants
        // (ex. « 4 161 037 ») d'être coupés sur deux lignes dans une colonne étroite.
        const fmtNum = (v) => (v || 0).toLocaleString('fr-FR').replace(/\s/g, ' ');
        const dateProd = pjMeta.dateProduction
            ? new Date(pjMeta.dateProduction).toLocaleDateString('fr-FR')
            : (dateRange?.[0]?.format('DD/MM/YYYY') || today);

        const titreJournalier = `TABLEAU JOURNALIER ( production du ${dateProd} )`;

        doc.addImage(logoCap, 'JPEG', 14, 10, 40, 16);
        doc.setFontSize(15);
        doc.setFont(undefined, 'bold');
        doc.text(titreJournalier, centerX, 35, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Agence sélectionnée dans le filtre (comme l'export État des règlements)
        const agenceNom = resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId });
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`AGENCE : ${agenceNom}`, centerX, 42, { align: 'center' });
        doc.setFont(undefined, 'normal');

        const headers = ['ASSURES', "DATE D'EFFET", "DATE D'ECHEANCE", 'ACCESS. COURTIER', 'ESPECES', 'CHEQUE', 'VIREMENT', 'IMPAYES', 'TOTAL CAISSE'];
        const numCell = (v) => ({ content: fmtNum(v), styles: { halign: 'right' } });
        const nomClient = (d) => (d.client?.type === 'entreprise'
            ? (d.client?.raisonSociale || d.client?.nom || '')
            : `${d.client?.nom || ''} ${d.client?.prenom || ''}`.trim()) || '-';

        // Regrouper les contrats par client (ordre de 1re apparition) : un client
        // avec plusieurs contrats voit son nom fusionné (rowSpan) sur ses lignes.
        const groupes = [];
        const idxParClient = new Map();
        data.forEach((d) => {
            const key = (d.client?._id || d.client || nomClient(d)).toString();
            if (!idxParClient.has(key)) { idxParClient.set(key, groupes.length); groupes.push([]); }
            groupes[idxParClient.get(key)].push(d);
        });

        const rows = [];
        groupes.forEach((groupe) => {
            groupe.forEach((d, i) => {
                const row = [];
                if (i === 0) {
                    row.push({ content: nomClient(d), rowSpan: groupe.length, styles: { halign: 'left', valign: 'middle' } });
                }
                row.push(
                    d.dateEffet ? new Date(d.dateEffet).toLocaleDateString('fr-FR') : '',
                    d.dateEcheance ? new Date(d.dateEcheance).toLocaleDateString('fr-FR') : '',
                    numCell(d.accessoiresCourtier), numCell(d.especes), numCell(d.cheque),
                    numCell(d.virement), numCell(d.impaye), numCell(d.totalCaisse),
                );
                rows.push(row);
            });
        });

        const tot = {
            accessoiresCourtier: data.reduce((s, d) => s + (d.accessoiresCourtier || 0), 0),
            especes: (pjMeta.totaux?.especes) ?? data.reduce((s, d) => s + (d.especes || 0), 0),
            cheque: (pjMeta.totaux?.cheque) ?? data.reduce((s, d) => s + (d.cheque || 0), 0),
            virement: (pjMeta.totaux?.virement) ?? data.reduce((s, d) => s + (d.virement || 0), 0),
            impaye: (pjMeta.totaux?.impaye) ?? data.reduce((s, d) => s + (d.impaye || 0), 0),
            totalCaisse: (pjMeta.totaux?.totalCaisse) ?? data.reduce((s, d) => s + (d.totalCaisse || 0), 0),
        };

        autoTable(doc, {
            startY: 49,
            head: [headers],
            body: rows,
            foot: [[
                { content: 'TOTAL', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.accessoiresCourtier), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.especes), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.cheque), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.virement), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.impaye), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
                { content: fmtNum(tot.totalCaisse), styles: { halign: 'right', fontStyle: 'bold', fillColor: false } },
            ]],
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, halign: 'center', valign: 'middle', fillColor: false },
            headStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.3 },
            footStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right', lineColor: [0, 0, 0], lineWidth: 0.3 },
            alternateRowStyles: { fillColor: false },
            columnStyles: { 0: { halign: 'left' } },
            margin: { left: 4, right: 4, bottom: 92 },
            tableWidth: pageWidth - 8,
            showFoot: 'lastPage',
        });

        const finalY = doc.lastAutoTable?.finalY || 100;
        const pageHeight = doc.internal.pageSize.height;

        // ── Pied de page (adresse + mentions légales), ancré en bas de page ──
        const footerLineY = pageHeight - 26;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.6);
        doc.line(14, footerLineY, pageWidth - 14, footerLineY);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        doc.text('Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28 / Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50', centerX, pageHeight - 20, { align: 'center' });
        doc.setFont(undefined, 'normal');
        doc.setFontSize(6);
        doc.text('RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com', centerX, pageHeight - 15, { align: 'center' });

        // ── Bloc « Signatures » (Établi par / Visa Direction) ──
        // Clampé sous le trait du pied de page pour ne JAMAIS le chevaucher.
        const sigBlockH = 40;
        const maxSigTop = footerLineY - sigBlockH - 6;
        const sigTop = Math.min(finalY + 26, maxSigTop);

        const boxW = 58;
        const boxH = 18;
        const gap = 20;
        const leftX = centerX - gap / 2 - boxW;
        const rightX = centerX + gap / 2;
        const leftC = leftX + boxW / 2;
        const rightC = rightX + boxW / 2;

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('SIGNATURES', centerX, sigTop, { align: 'center' });

        doc.setFontSize(9);
        doc.text('ÉTABLI PAR', leftC, sigTop + 9, { align: 'center' });
        doc.text('VISA DE LA DIRECTION', rightC, sigTop + 9, { align: 'center' });
        doc.setFont(undefined, 'normal');

        doc.setDrawColor(120, 120, 120);
        doc.setLineWidth(0.3);
        doc.rect(leftX, sigTop + 12, boxW, boxH);
        doc.rect(rightX, sigTop + 12, boxW, boxH);

        if (exporterName) {
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text(exporterName.toUpperCase(), leftC, sigTop + 36, { align: 'center' });
            doc.setFont(undefined, 'normal');
        }
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text('Nom, signature & cachet', rightC, sigTop + 36, { align: 'center' });
        doc.setTextColor(0, 0, 0);

        addExporterFooter(doc, exporterName);
        const baseName = activeTab === 'production-jour' ? 'production_du_jour' : 'tableau_journalier';
        doc.save(`${baseName}_${dateProd.replace(/\//g, '-')}.pdf`);
        toast.success(activeTab === 'production-jour' ? 'Production du jour exportée avec succès' : 'Tableau journalier exporté avec succès');
    };

    const handleExportPDF = () => {
        if (activeTab === 'point-journalier' || activeTab === 'production-jour') return handleExportPDFTableauJournalier();

        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        const { headers, rows, foot, numericCols = [] } = getExportConfig();
        const title = tabTitles[activeTab] || 'BILAN';

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        const agenceNom = resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId });
        doc.setFontSize(12);
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        const titreFinal = buildTitreExport(title);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text(titreFinal, centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Période de filtre (si renseignée)
        let tableStartY = 38;
        if (dateRange && dateRange[0] && dateRange[1]) {
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 38);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 38);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 38);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 38);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            tableStartY = 44;
        }

        const footRow = foot && foot.length > 0
            ? [foot]
            : [[{ content: `Total: ${rows.length} ligne(s)`, colSpan: headers.length, styles: { halign: 'right', fontStyle: 'bold', fillColor: false } }]];

        const columnStyles = numericCols.reduce((acc, idx) => {
            acc[idx] = { halign: 'right' };
            return acc;
        }, {});

        autoTable(doc, {
            startY: tableStartY,
            head: [headers],
            body: rows,
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3 },
            headStyles: PDF_HEAD_STYLES,
            footStyles: PDF_FOOT_STYLES,
            alternateRowStyles: { fillColor: [245, 247, 250] },
            columnStyles,
            foot: footRow,
            showFoot: 'lastPage',
            margin: { bottom: 20 },
        });

        addExporterFooter(doc, exporterName);
        const fileName = activeTab.replace(/-/g, '_');
        doc.save(`${fileName}_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    // Export Excel "Production du jour" / "Point journalier" — même format que le PDF
    // (TABLEAU JOURNALIER : Assurés, Date d'effet, Date d'échéance, Access. courtier,
    // Espèces, Chèque, Impayés, Total caisse + ligne TOTAL).
    const handleExportExcelTableauJournalier = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const dateProd = pjMeta.dateProduction
            ? new Date(pjMeta.dateProduction).toLocaleDateString('fr-FR')
            : (dateRange?.[0]?.format('DD/MM/YYYY') || today);
        const titreJournalier = `TABLEAU JOURNALIER ( production du ${dateProd} )`;

        const headers = ['ASSURES', "DATE D'EFFET", "DATE D'ECHEANCE", 'ACCESS. COURTIER', 'ESPECES', 'CHEQUE', 'VIREMENT', 'IMPAYES', 'TOTAL CAISSE'];
        const widths = [30, 16, 16, 18, 14, 12, 14, 12, 16];
        // Indices (1-based) des colonnes numériques
        const numCols1 = [4, 5, 6, 7, 8, 9];

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('PRODUCTION DU JOUR');
        ws.columns = widths.map((w) => ({ width: w }));
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([titreJournalier]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        addExcelExportInfo(ws, { colCount: headers.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        const nomClient = (d) => (d.client?.type === 'entreprise'
            ? (d.client?.raisonSociale || d.client?.nom || '')
            : `${d.client?.nom || ''} ${d.client?.prenom || ''}`.trim()) || '-';

        // Regrouper les contrats par client (ordre de 1re apparition) ; le nom de
        // l'assuré est fusionné verticalement sur toutes les lignes de ses contrats.
        const groupes = [];
        const idxParClient = new Map();
        data.forEach((d) => {
            const key = (d.client?._id || d.client || nomClient(d)).toString();
            if (!idxParClient.has(key)) { idxParClient.set(key, groupes.length); groupes.push([]); }
            groupes[idxParClient.get(key)].push(d);
        });

        groupes.forEach((groupe) => {
            let firstRowNum = null;
            let lastRowNum = null;
            groupe.forEach((d, i) => {
                const row = ws.addRow([
                    i === 0 ? nomClient(d) : '',
                    d.dateEffet ? new Date(d.dateEffet).toLocaleDateString('fr-FR') : '',
                    d.dateEcheance ? new Date(d.dateEcheance).toLocaleDateString('fr-FR') : '',
                    d.accessoiresCourtier || 0,
                    d.especes || 0,
                    d.cheque || 0,
                    d.virement || 0,
                    d.impaye || 0,
                    d.totalCaisse || 0,
                ]);
                if (firstRowNum === null) firstRowNum = row.number;
                lastRowNum = row.number;
                row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
                row.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
                numCols1.forEach((c) => {
                    row.getCell(c).numFmt = '#,##0';
                    row.getCell(c).alignment = { horizontal: 'right', vertical: 'middle' };
                });
            });
            if (lastRowNum > firstRowNum) {
                ws.mergeCells(firstRowNum, 1, lastRowNum, 1);
                ws.getCell(firstRowNum, 1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            }
        });

        const tot = {
            accessoiresCourtier: data.reduce((s, d) => s + (d.accessoiresCourtier || 0), 0),
            especes: (pjMeta.totaux?.especes) ?? data.reduce((s, d) => s + (d.especes || 0), 0),
            cheque: (pjMeta.totaux?.cheque) ?? data.reduce((s, d) => s + (d.cheque || 0), 0),
            virement: (pjMeta.totaux?.virement) ?? data.reduce((s, d) => s + (d.virement || 0), 0),
            impaye: (pjMeta.totaux?.impaye) ?? data.reduce((s, d) => s + (d.impaye || 0), 0),
            totalCaisse: (pjMeta.totaux?.totalCaisse) ?? data.reduce((s, d) => s + (d.totalCaisse || 0), 0),
        };

        const totalRow = ws.addRow(['TOTAL', '', '', tot.accessoiresCourtier, tot.especes, tot.cheque, tot.virement, tot.impaye, tot.totalCaisse]);
        ws.mergeCells(totalRow.number, 1, totalRow.number, 3);
        totalRow.getCell(1).value = 'TOTAL';
        totalRow.getCell(1).font = { bold: true };
        totalRow.getCell(1).alignment = { horizontal: 'right' };
        numCols1.forEach((c) => {
            totalRow.getCell(c).numFmt = '#,##0';
            totalRow.getCell(c).font = { bold: true };
            totalRow.getCell(c).alignment = { horizontal: 'right' };
        });

        borderExcelTable(ws, headerRow.number, headers.length);
        const buffer = await wb.xlsx.writeBuffer();
        const baseName = activeTab === 'production-jour' ? 'production_du_jour' : 'tableau_journalier';
        saveAs(new Blob([buffer]), `${baseName}_${dateProd.replace(/\//g, '-')}.xlsx`);
        toast.success(activeTab === 'production-jour' ? 'Production du jour exportée avec succès' : 'Tableau journalier exporté avec succès');
    };

    const handleExportExcel = async () => {
        if (activeTab === 'point-journalier' || activeTab === 'production-jour') return handleExportExcelTableauJournalier();

        const today = new Date().toLocaleDateString('fr-FR');
        const { headers, rows, widths, numericCols = [] } = getExportConfig();
        const title = tabTitles[activeTab] || 'BILAN';
        const titreFinal = buildTitreExport(title);
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet(title);
        ws.columns = widths.map((w) => ({ width: w }));
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([titreFinal]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (dateRange && dateRange[0] && dateRange[1]) {
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, headers.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: headers.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        rows.forEach((r) => ws.addRow(r));

        addExcelTotals(ws, { headerRow: headerRow.number, numericCols, labelCol: 1 });
        borderExcelTable(ws, headerRow.number, headers.length);
        const buffer = await wb.xlsx.writeBuffer();
        const fileName = activeTab.replace(/-/g, '_');
        saveAs(new Blob([buffer]), `${fileName}_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    const tabItems = [
        { key: 'bordereau-courtier',  label: <span><FileTextOutlined /> {isMobile ? 'Courtier' : 'Bordereau courtier'}</span> },
        { key: 'bordereau-assureurs', label: <span><BankOutlined /> {isMobile ? 'Assureurs' : 'Bordereau assureurs'}</span> },
        { key: 'clients-actifs',      label: <span><TeamOutlined /> {isMobile ? 'Clients' : 'Clients actifs'}</span> },
        { key: 'point-journalier',    label: <span><CalendarOutlined /> {isMobile ? 'Journalier' : 'Point journalier'}</span> },
        { key: 'production-jour',     label: <span><BarChartOutlined /> {isMobile ? 'Production' : 'Production du jour'}</span> },
        { key: 'paiements-periode',   label: <span><WalletOutlined /> {isMobile ? 'Paiements' : 'Paiements période'}</span> },
    ];

    // ─── KPIs dynamiques par tab ──────────────────────────────────────
    const summaryStats = useMemo(() => {
        switch (activeTab) {
            case 'bordereau-courtier': {
                const totalPrimes = data.reduce((s, d) => s + (d.primeNette || 0), 0);
                const totalComm = data.reduce((s, d) => s + (d.commission || 0), 0);
                const nbVerses = data.filter((d) => d.estVerse).length;
                return [
                    { icon: <FileTextOutlined />, label: 'Contrats', value: data.length, tone: 'blue' },
                    { icon: <DollarOutlined />, label: 'Total primes nettes', value: formatMontant(totalPrimes), tone: 'indigo' },
                    { icon: <DollarOutlined />, label: 'Total commissions', value: formatMontant(totalComm), tone: 'green' },
                    { icon: <CheckCircleOutlined />, label: 'Versés', value: nbVerses, tone: 'teal' },
                ];
            }
            case 'bordereau-assureurs': {
                const tp = data.reduce((s, d) => s + (d.totalPrimes || 0), 0);
                const tc = data.reduce((s, d) => s + (d.totalCommissions || 0), 0);
                const nr = data.reduce((s, d) => s + (d.netAReverser || 0), 0);
                return [
                    { icon: <BankOutlined />, label: 'Compagnies', value: data.length, tone: 'blue' },
                    { icon: <DollarOutlined />, label: 'Total primes', value: formatMontant(tp), tone: 'indigo' },
                    { icon: <DollarOutlined />, label: 'Total commissions', value: formatMontant(tc), tone: 'green' },
                    { icon: <ExclamationCircleOutlined />, label: 'Net à reverser', value: formatMontant(nr), tone: 'red' },
                ];
            }
            case 'clients-actifs': {
                const tp2 = data.reduce((s, d) => s + (d.totalPrimes || 0), 0);
                const totalContrats = data.reduce((s, d) => s + (d.nbContratsActifs || 0), 0);
                return [
                    { icon: <TeamOutlined />, label: 'Clients actifs', value: data.length, tone: 'blue' },
                    { icon: <FileTextOutlined />, label: 'Contrats actifs', value: totalContrats, tone: 'indigo' },
                    { icon: <DollarOutlined />, label: 'Total primes', value: formatMontant(tp2), tone: 'green' },
                ];
            }
            case 'point-journalier': {
                const tp3 = data.reduce((s, d) => s + (d.primeTTC || 0), 0);
                return [
                    { icon: <CalendarOutlined />, label: 'Contrats du jour', value: data.length, tone: 'blue' },
                    { icon: <DollarOutlined />, label: 'Total TTC', value: formatMontant(tp3), tone: 'indigo' },
                ];
            }
            case 'production-jour': {
                const tp3b = data.reduce((s, d) => s + (d.primeTTC || 0), 0);
                return [
                    { icon: <BarChartOutlined />, label: 'Contrats produits', value: data.length, tone: 'blue' },
                    { icon: <DollarOutlined />, label: 'Total TTC', value: formatMontant(tp3b), tone: 'indigo' },
                ];
            }
            case 'paiements-periode': {
                const tp4 = data.reduce((s, d) => s + (d.montant || 0), 0);
                return [
                    { icon: <WalletOutlined />, label: 'Paiements', value: data.length, tone: 'blue' },
                    { icon: <DollarOutlined />, label: 'Total encaissé', value: formatMontant(tp4), tone: 'green' },
                ];
            }
            default: return [];
        }
    }, [activeTab, data]);

    return (
        <div className="bilans-page">
            {/* Header */}
            <header className="bilans-header">
                <div className="bilans-header-text">
                    <h1 className="bilans-title">
                        <BarChartOutlined /> Bilans &amp; États
                    </h1>
                    <p className="bilans-subtitle">Rapports comptables et bordereaux</p>
                </div>
                <div className="bilans-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        Guide
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Bilans &amp; États</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={640}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page regroupe les <strong>rapports comptables et bordereaux</strong> du
                    cabinet. Choisissez un onglet selon le document à produire.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    const noteStyle = {
                        marginTop: 8, padding: '10px 12px', background: 'rgba(200,16,46,0.07)',
                        border: '1px solid rgba(200,16,46,0.25)', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.45,
                    };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <BarChartOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs &amp; filtres</strong> — En haut : nombre
                                    de contrats, total primes nettes, commissions et versés. Affinez
                                    avec la <strong>période</strong>, l'<strong>agence</strong>, la
                                    <strong> compagnie</strong> et le <strong>type d'assurance</strong>
                                    (ex. Automobile uniquement) ; « Effacer » réinitialise.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <FileTextOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Bordereau courtier</strong> — Liste des contrats et
                                    des commissions revenant au cabinet, sur la période choisie.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <BankOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Bordereau assureurs</strong> — Cochez les contrats
                                    <strong> payés</strong> à reverser à la compagnie, puis cliquez sur
                                    <strong> « Verser au bordereau assureur »</strong>.
                                    <div style={noteStyle}>
                                        <strong style={{ color: '#C8102E' }}>Important :</strong> les
                                        contrats <em>impayés</em> et ceux <em>déjà versés</em> sont
                                        désactivés (non sélectionnables). Le statut passe à
                                        « Versé » une fois l'opération confirmée.
                                    </div>
                                </div>
                            </div>
                            <div style={liStyle}>
                                <TeamOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Autres onglets</strong> — <strong>Clients actifs</strong>
                                    (portefeuille par client), <strong>Point journalier</strong>
                                    (contrats selon leur <em>date de saisie</em>),
                                    <strong> Production du jour</strong> (contrats selon leur
                                    <em> date de production</em> réelle — tableau journalier qui gère
                                    les contrats antidatés saisis a posteriori) et
                                    <strong> Paiements période</strong> (encaissements sur une durée).
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de l'onglet courant, en tenant compte des filtres
                                    (période, agence, compagnie, type d'assurance).
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            {summaryStats.length > 0 && (
                <section
                    className={`bilans-kpis bilans-kpis-${summaryStats.length}`}
                    aria-label="Indicateurs"
                >
                    {summaryStats.map((stat, idx) => (
                        <MiniKpiCard
                            key={idx}
                            icon={stat.icon}
                            label={stat.label}
                            value={stat.value}
                            tone={stat.tone}
                        />
                    ))}
                </section>
            )}

            {/* Carte principale */}
            <Card className="bilans-main-card">
                <div className="bilans-toolbar">
                    <RangePicker
                        format="DD/MM/YYYY"
                        onChange={(dates) => setDateRange(dates ? [toMoment(dates[0]), toMoment(dates[1])] : null)}
                        value={dateRange ? [toDayjs(dateRange[0]), toDayjs(dateRange[1])] : null}
                        className="bilans-filter-dates"
                        placeholder={['Date début', 'Date fin']}
                        allowClear
                    />
                    {canSwitchAgence && (
                        <Select
                            value={selectedAgence}
                            onChange={setSelectedAgence}
                            className="bilans-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => <Option key={a._id} value={a._id}>{a.nom}</Option>)}
                        </Select>
                    )}
                    <Select
                        value={selectedCompagnie}
                        onChange={setSelectedCompagnie}
                        className="bilans-filter"
                        aria-label="Filtrer par compagnie"
                    >
                        <Option value="all">Toutes les compagnies</Option>
                        {compagnies.map((c) => <Option key={c._id} value={c._id}>{c.nom}</Option>)}
                    </Select>
                    <Select
                        value={selectedType}
                        onChange={setSelectedType}
                        className="bilans-filter"
                        aria-label="Filtrer par type d'assurance"
                    >
                        <Option value="all">Tous les types d'assurance</Option>
                        {typesAssurance.map((t) => <Option key={t._id} value={t._id}>{t.nom}</Option>)}
                    </Select>
                    {activeFiltersCount > 0 && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="bilans-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="bilans-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    className="bilans-tabs"
                />

                {/* Bandeau d'action pour bordereau-courtier */}
                {activeTab === 'bordereau-courtier' && (
                    <div className="bilans-action-bar">
                        <span className="bilans-action-text">
                            {selectedRowKeys.length > 0
                                ? <>{selectedRowKeys.length} contrat{selectedRowKeys.length > 1 ? 's' : ''} sélectionné{selectedRowKeys.length > 1 ? 's' : ''} — éligibles : <strong>{eligiblesCount}</strong></>
                                : <>Sélectionnez les contrats payés à verser au bordereau assureur (impayés et déjà versés sont désactivés)</>}
                        </span>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => { setVersementDate(moment()); setVersementModalOpen(true); }}
                            className="bilans-btn-verser"
                        >
                            Verser au bordereau assureur
                        </Button>
                    </div>
                )}

                {/* Table */}
                <Table
                    dataSource={data}
                    columns={getColumns()}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    rowSelection={rowSelection}
                    expandable={activeTab === 'bordereau-assureurs' ? {
                        expandedRowRender: (record) => renderVersementsDetails(record),
                        rowExpandable: (record) => Array.isArray(record.versements) && record.versements.length > 0,
                    } : undefined}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune donnée pour cette période" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 25,
                        showTotal: (t) => `${t} ligne${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />

                {/* Modal versement */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <SendOutlined style={{ color: '#C8102E' }} />
                            <span>Verser au bordereau assureur</span>
                        </div>
                    }
                    open={versementModalOpen}
                    onCancel={() => setVersementModalOpen(false)}
                    onOk={handleVerser}
                    confirmLoading={versementSubmitting}
                    okText="Confirmer le versement"
                    cancelText="Annuler"
                    okButtonProps={{ icon: <SendOutlined />, danger: true }}
                    width={680}
                    destroyOnClose
                    style={{ top: 150 }}
                >
                    {(() => {
                        const selectedRows = data.filter((d) => selectedRowKeys.includes(d._id) && d.estPaye && !d.estVerse);
                        const rejected = selectedRowKeys.length - selectedRows.length;
                        const byCompagnie = {};
                        let totalReverse = 0;
                        let totalCommission = 0;
                        selectedRows.forEach((r) => {
                            const key = r.compagnie || 'Sans compagnie';
                            if (!byCompagnie[key]) byCompagnie[key] = { count: 0, montantReverse: 0, commission: 0, primeNette: 0 };
                            byCompagnie[key].count += 1;
                            byCompagnie[key].montantReverse += r.montantReverseAssureur || 0;
                            byCompagnie[key].commission += r.commission || 0;
                            byCompagnie[key].primeNette += r.primeNette || 0;
                            totalReverse += r.montantReverseAssureur || 0;
                            totalCommission += r.commission || 0;
                        });
                        const compagniesArr = Object.entries(byCompagnie);
                        const nbCompagnies = compagniesArr.length;

                        return (
                            <div>
                                <div className="versement-summary">
                                    <Row gutter={[16, 8]}>
                                        <Col xs={12} sm={6}>
                                            <div className="versement-stat-label">Contrats éligibles</div>
                                            <div className="versement-stat-value blue">{selectedRows.length}</div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="versement-stat-label">Bordereaux générés</div>
                                            <div className="versement-stat-value blue">{nbCompagnies}</div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="versement-stat-label">Commission retenue</div>
                                            <div className="versement-stat-value green">{formatMontant(totalCommission)}</div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="versement-stat-label">Total reversé</div>
                                            <div className="versement-stat-value red">{formatMontant(totalReverse)}</div>
                                        </Col>
                                    </Row>
                                </div>

                                {rejected > 0 && (
                                    <div className="versement-warn">
                                        ⚠️ <b>{rejected}</b> contrat(s) sélectionné(s) seront écartés (impayés ou déjà versés).
                                    </div>
                                )}

                                {nbCompagnies > 1 && (
                                    <div className="versement-info">
                                        💡 <b>{nbCompagnies} bordereaux distincts</b> seront générés (un par compagnie).
                                    </div>
                                )}

                                <div className="versement-recap-title">Récapitulatif par compagnie</div>
                                <Table
                                    size="small"
                                    dataSource={compagniesArr.map(([nom, agg], i) => ({ key: i, nom, ...agg }))}
                                    pagination={false}
                                    scroll={{ y: 220 }}
                                    className="versement-recap-table"
                                    columns={[
                                        { title: 'Compagnie', dataIndex: 'nom', key: 'nom', render: (t) => <strong>{t}</strong> },
                                        { title: 'Contrats', dataIndex: 'count', key: 'count', align: 'center', width: 80 },
                                        { title: 'Prime nette', dataIndex: 'primeNette', key: 'primeNette', align: 'right', render: (v) => formatMontant(v || 0) },
                                        { title: 'Commission', dataIndex: 'commission', key: 'commission', align: 'right', render: (v) => <span style={{ color: '#16a34a' }}>{formatMontant(v || 0)}</span> },
                                        { title: 'Reverser', dataIndex: 'montantReverse', key: 'montantReverse', align: 'right', render: (v) => <strong style={{ color: '#C8102E' }}>{formatMontant(v || 0)}</strong> },
                                    ]}
                                />

                                <div className="versement-date-block">
                                    <div className="versement-stat-label">Date du versement</div>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={toDayjs(versementDate)}
                                        onChange={(d) => setVersementDate(toMoment(d))}
                                        allowClear={false}
                                        disabledDate={(d) => d && d.valueOf() > moment().endOf('day').valueOf()}
                                    />
                                    <div className="versement-date-hint">
                                        La date du versement ne peut pas être postérieure à aujourd'hui.
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </Modal>
            </Card>
        </div>
    );
};

export default Bilans;
